import { useCallback, useId, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { Card } from './Card'
import {
  addDays,
  covers,
  dayFormat,
  dayOf,
  monthGridDays,
  monthGridStart,
  placeItems,
  resolveLocale,
  utcNoon,
  type CalendarDay,
  type CalendarFormat,
  type CalendarLegendEntry,
  type CalendarMarker,
  type DatedItem,
} from './calendar'
import { cn, focusRing } from './cn'
import { ChevronLeft, ChevronRight } from './icons'
import { Select } from './Select'

/** How far the month can move from today's: six months back and six forward. */
const monthWindow = 6

/** The first day of the month `offset` months from `day`'s. */
function monthStart(day: CalendarDay, offset: number): CalendarDay {
  const date = utcNoon(day)
  date.setUTCDate(1)
  date.setUTCMonth(date.getUTCMonth() + offset)
  return date.toISOString().slice(0, 10)
}

// `aria-disabled` rather than `disabled` at the window's ends, so the button keeps focus.
const stepButtonClass = cn(
  'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded border border-text-light text-text-secondary transition hover:border-text-secondary hover:text-text-primary',
  'aria-disabled:cursor-not-allowed aria-disabled:opacity-40',
  focusRing,
)

export interface MiniCalendarProps extends CalendarFormat {
  /**
   * A dot, or a ring for an item marked so, marks each day an item falls on, the days from the
   * months before and after that fill the grid included, so give the whole grid's items.
   */
  readonly items: readonly DatedItem[]
  /** Highlighted, and the month shown first. Now, by default. */
  readonly today?: Date
  readonly labels: {
    readonly previousMonth: string
    readonly nextMonth: string
    /** The month picker's accessible name. */
    readonly month: string
  }
  /** The small caption over the grid, such as "at a glance". */
  readonly caption?: string
  /** What the marks mean: one line per mark, or a text for the dot alone, such as "Class session". */
  readonly legend?: string | readonly CalendarLegendEntry[]
  /** Called with the month shown, `YYYY-MM`, when it changes, so its items can be loaded. */
  readonly onMonthChange?: (month: string) => void
  /**
   * The month shown, `YYYY-MM`, when the page holds it and moves it from `onMonthChange`; else
   * today's, moved by the buttons and the picker.
   */
  readonly month?: string | undefined
}

/** How many months `month` (`YYYY-MM`) is after `day`'s. */
function monthsFrom(day: CalendarDay, month: string): number {
  const [fromYear = 0, fromMonth = 1] = day.split('-').map(Number)
  const [toYear = 0, toMonth = 1] = month.split('-').map(Number)
  return (toYear - fromYear) * 12 + (toMonth - fromMonth)
}

/** A day's mark, as the mockups' `MiniCalendar` draws it: a filled dot or a hollow ring; in the legend a little larger. */
function Marker(props: { readonly marker: CalendarMarker; readonly isLegend?: boolean }) {
  return props.marker === 'ring' ? (
    <span className={cn('shrink-0 rounded-full border border-text-secondary', props.isLegend === true ? 'h-2 w-2' : 'h-1.5 w-1.5')} />
  ) : (
    <span className={cn('shrink-0 rounded-full bg-success-main', props.isLegend === true ? 'h-2 w-2' : 'h-1 w-1')} />
  )
}

/**
 * A month grid with a month picker and previous and next buttons. Today is outlined, the
 * month's days are filled, and a day with an item carries a dot. It is a section under a hidden
 * heading; a month change is announced, and each day outside the month shown says so.
 */
export function MiniCalendar({
  items,
  today,
  labels,
  caption,
  legend,
  onMonthChange,
  month: heldMonth,
  locale: requestedLocale,
  timeZone,
}: MiniCalendarProps) {
  const intl = useIntl()
  const headingId = useId()
  const locale = resolveLocale(requestedLocale)
  const [ownOffset, setOwnOffset] = useState(0)
  const todayDay = useMemo(() => dayOf(today ?? new Date(), timeZone), [today, timeZone])
  const placed = useMemo(() => placeItems(items, timeZone), [items, timeZone])
  const offset = heldMonth === undefined ? ownOffset : monthsFrom(todayDay, heldMonth)
  // The window around today's month, widened to take a month the page holds outside it.
  const earliest = Math.min(-monthWindow, offset)
  const latest = Math.max(monthWindow, offset)

  const changeOffset = useCallback(
    (next: number) => {
      const clamped = Math.max(earliest, Math.min(latest, next))
      if (clamped === offset) {
        return
      }
      if (heldMonth === undefined) {
        setOwnOffset(clamped)
      }
      onMonthChange?.(monthStart(todayDay, clamped).slice(0, 7))
    },
    [earliest, heldMonth, latest, offset, onMonthChange, todayDay],
  )
  const previous = useCallback(() => changeOffset(offset - 1), [changeOffset, offset])
  const next = useCallback(() => changeOffset(offset + 1), [changeOffset, offset])

  const monthOptions = useMemo(() => {
    const format = dayFormat(locale, { month: 'long', year: 'numeric' })
    return Array.from({ length: latest - earliest + 1 }, (_, index) => {
      const value = earliest + index
      return { value, label: format.format(utcNoon(monthStart(todayDay, value))) }
    })
  }, [earliest, latest, locale, todayDay])

  const fullDay = useMemo(
    () => dayFormat(locale, { weekday: 'long', month: 'long', day: 'numeric' }),
    [locale],
  )
  const list = useMemo(() => new Intl.ListFormat(locale, { type: 'conjunction' }), [locale])

  const weekdays = useMemo(() => {
    const format = dayFormat(locale, { weekday: 'narrow' })
    // Any week will do; 2026-01-04 is a Sunday.
    return Array.from({ length: 7 }, (_, index) => {
      const day = addDays('2026-01-04', index)
      return { day, label: format.format(utcNoon(day)) }
    })
  }, [locale])

  const cells = useMemo(() => {
    const month = monthStart(todayDay, offset).slice(0, 7)
    const gridStart = monthGridStart(month)
    return Array.from({ length: monthGridDays }, (_, index) => {
      const day = addDays(gridStart, index)
      const inMonth = day.startsWith(month)
      const onDay = placed.filter((item) => covers(item, day))
      return {
        day,
        date: Number(day.slice(8)),
        inMonth,
        isToday: day === todayDay,
        titles: onDay.map((item) => item.title),
        markers: (['dot', 'ring'] as const).filter((marker) => onDay.some((item) => (item.marker ?? 'dot') === marker)),
      }
    })
  }, [offset, placed, todayDay])
  const legendEntries = useMemo<readonly CalendarLegendEntry[]>(
    () => (typeof legend === 'string' ? [{ marker: 'dot', label: legend }] : (legend ?? [])),
    [legend],
  )

  const shownMonth = monthOptions.find((option) => option.value === offset)?.label ?? ''

  return (
    <section aria-labelledby={headingId}>
      <Card className="p-4">
        <h2 id={headingId} className="sr-only">
          {intl.formatMessage({ id: 'ui.miniCalendar.title' })}
        </h2>
        {/* Says the month once the buttons or the picker change it; silent on the first render. */}
        <output aria-live="polite" className="sr-only">
          {shownMonth}
        </output>
        <div className="mb-4 flex items-center gap-2">
          <button
            type="button"
            onClick={previous}
            aria-label={labels.previousMonth}
            aria-disabled={offset === earliest}
            className={stepButtonClass}
          >
            <ChevronLeft size={16} />
          </button>
          <Select
            label={labels.month}
            hideLabel
            value={offset}
            onChange={changeOffset}
            options={monthOptions}
            size="small"
            className="flex-1"
            // The month names are in the requested locale; the menu renders outside this page.
            {...(requestedLocale === undefined ? {} : { lang: requestedLocale })}
          />
          <button
            type="button"
            onClick={next}
            aria-label={labels.nextMonth}
            aria-disabled={offset === latest}
            className={stepButtonClass}
          >
            <ChevronRight size={16} />
          </button>
        </div>
        {caption !== undefined && (
          <p className="mb-3 text-right text-xs text-text-secondary">{caption}</p>
        )}

        {/* Weekday letters and bare numbers are visual only; each cell reads its full date. */}
        <div className="grid grid-cols-7 gap-1.5 text-center">
          {weekdays.map((weekday) => (
            <span key={weekday.day} aria-hidden="true" className="text-xs font-semibold text-text-light">
              {weekday.label}
            </span>
          ))}
          {cells.map((cell) => (
            <span
              key={cell.day}
              aria-current={cell.isToday ? 'date' : undefined}
              className={cn(
                'flex h-9 flex-col items-center justify-center gap-0.5 rounded text-sm',
                cell.isToday
                  ? 'border border-text-primary bg-background font-semibold text-text-primary'
                  : cell.inMonth
                    ? 'bg-background-contrast-sm text-text-primary'
                    : 'text-text-light',
              )}
            >
              <span aria-hidden="true">{cell.date}</span>
              <span className="sr-only">
                {cell.inMonth
                  ? fullDay.format(utcNoon(cell.day))
                  : intl.formatMessage({ id: 'ui.miniCalendar.otherMonthDay' }, { day: fullDay.format(utcNoon(cell.day)) })}
              </span>
              {cell.titles.length > 0 && (
                <>
                  <span className="flex items-center gap-0.5">
                    {cell.markers.map((marker) => (
                      <Marker key={marker} marker={marker} />
                    ))}
                  </span>
                  <span className="sr-only">{list.format(cell.titles)}</span>
                </>
              )}
            </span>
          ))}
        </div>

        {legendEntries.length > 0 && (
          <div className="mt-3 flex flex-col gap-1 border-t border-divider pt-3 text-xs text-text-secondary">
            {legendEntries.map((entry) => (
              <span key={`${entry.marker}-${entry.label}`} className="flex items-center gap-1.5">
                <Marker marker={entry.marker} isLegend />
                {entry.label}
              </span>
            ))}
          </div>
        )}
      </Card>
    </section>
  )
}
